import { useDashboardReview } from './useDashboardReview'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { AppDispatch } from 'config/redux'
import keys from 'constants/keys'
import { updateResults } from 'redux/actions/tasks_v2'
import { TaskOverviewContentType, useTaskOverviewContext } from '../TaskOverviewStackProvider'
import {
  AnswerDraft,
  answerPayload,
  draftFromResult,
  findExecutionResult,
  hasDraftAnswer,
  hasAnswerChanges,
  isExecutionComplete,
  isMediaAnswer,
  nextUnanswered,
} from '../model/execution'

export function useExecutionWorkspace() {
  const context = useTaskOverviewContext()
  const review = useDashboardReview()
  const { task, resultContextId, isReadMode, isSubtaskBusy, setIsSubtaskBusy } = context
  const dispatch: AppDispatch = useDispatch()
  const source = useSelector((state: RootState) => state.tasks_v2.subtasks[task.tid])
  const results = useSelector((state: RootState) => state.tasks_v2.allResults[task.tid])
  const pollLoading = useSelector((state: RootState) => state.tasks_v2.allResultsLoading)
  const allSubtasks = useMemo(() => [...(source || [])].sort((a, b) => a.order_index - b.order_index), [source])
  const subtasks = review?.subtasks || allSubtasks
  const [selectedId, select] = useState<string | null>(null)
  const [drafts, updateDrafts] = useState<Record<string, AnswerDraft>>(
    () => review?.answerDrafts.current[resultContextId] || {}
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const saveLock = useRef(false)
  const mounted = useRef(true)
  const scope = JSON.stringify([task.tid, resultContextId, task.recurrence_ts])
  const activeScope = useRef(scope)
  activeScope.current = scope
  const selected = subtasks.find((subtask) => subtask.st_id === selectedId)
  const resultFor = (id: string) => findExecutionResult(results || [], id, resultContextId, task.recurrence_ts)
  const completed = new Set(
    allSubtasks.filter((subtask) => isExecutionComplete(resultFor(subtask.st_id))).map((subtask) => subtask.st_id)
  )
  const remaining = allSubtasks.filter((subtask) => subtask.is_required && !completed.has(subtask.st_id))
  const hasUnsavedAnswers = allSubtasks.some(
    (subtask) => !!drafts[subtask.st_id] && hasAnswerChanges(subtask, drafts[subtask.st_id], resultFor(subtask.st_id))
  )
  const draft = selected ? drafts[selected.st_id] || draftFromResult(resultFor(selected.st_id)) : {}
  const dirty = !!selected && !!drafts[selected.st_id] && hasAnswerChanges(selected, draft, resultFor(selected.st_id))
  const hasAnswer = !!selected && hasDraftAnswer(selected, draft)
  const canDone =
    !!selected && (isReadMode || !selected.is_required || hasAnswer || (!dirty && completed.has(selected.st_id)))
  const busy = saving || isSubtaskBusy
  const isPollSelection =
    !!selected && [keys.SUBTASK_TYPES.POLL_SUBTASK, keys.SUBTASK_TYPES.PRIVATE_POLL].includes(selected.type)

  const setDrafts = (update: (previous: Record<string, AnswerDraft>) => Record<string, AnswerDraft>) => {
    updateDrafts((previous) => {
      const next = update(previous)
      if (review) review.answerDrafts.current[resultContextId] = next
      return next
    })
  }

  const showSubtask = (id: string | null) => {
    select(id)
    setError(false)
    const target = subtasks.find((subtask) => subtask.st_id === id)
    if (target && isMediaAnswer(target)) context.navigateToSubtask(target)
  }

  const choose = (id: string | null) => {
    if (!busy && !saveLock.current) {
      showSubtask(id)
    }
  }

  const savePoll = async (nextDraft: AnswerDraft) => {
    if (!selected || !isPollSelection || pollLoading || isReadMode || busy || saveLock.current) return
    const currentScope = scope
    const isCurrent = () => mounted.current && activeScope.current === currentScope
    const clearDraft = () =>
      setDrafts((previous) => {
        const next = { ...previous }
        delete next[selected.st_id]
        return next
      })

    setError(false)
    // Re-selecting a persisted answer must not reset its approval/review status.
    if (!hasAnswerChanges(selected, nextDraft, resultFor(selected.st_id))) {
      clearDraft()
      return
    }

    saveLock.current = true
    setSaving(true)
    setIsSubtaskBusy(true)
    try {
      const ok = await dispatch(
        updateResults({
          tid: task.tid,
          st_id: selected.st_id,
          type: selected.type,
          resultContextId,
          recurrence_ts: task.recurrence_ts,
          status: hasDraftAnswer(selected, nextDraft) ? keys.TASK_STATUSES.DONE.value : keys.TASK_STATUSES.TODO.value,
          ...answerPayload(selected, nextDraft),
          optimistic: false,
        })
      )
      if (!isCurrent()) return
      if (ok) clearDraft()
      else setError(true)
    } catch {
      if (isCurrent()) setError(true)
    } finally {
      if (isCurrent()) {
        saveLock.current = false
        setSaving(false)
        setIsSubtaskBusy(false)
      }
    }
  }

  const change = (patch: Partial<AnswerDraft>) => {
    if (!selected || isReadMode || busy || saveLock.current || (isPollSelection && pollLoading)) return
    const nextDraft = { ...draft, ...patch }
    setDrafts((previous) => ({ ...previous, [selected.st_id]: nextDraft }))
    if (isPollSelection) void savePoll(nextDraft)
  }

  const retryPoll = () => savePoll(draft)

  const done = async () => {
    if (isPollSelection) return retryPoll()
    if (!selected || !canDone || busy || saveLock.current) return
    saveLock.current = true
    setSaving(true)
    setIsSubtaskBusy(true)
    setError(false)
    try {
      if (!isReadMode && dirty) {
        const ok = await dispatch(
          updateResults({
            tid: task.tid,
            st_id: selected.st_id,
            type: selected.type,
            resultContextId,
            recurrence_ts: task.recurrence_ts,
            status: hasAnswer ? keys.TASK_STATUSES.DONE.value : keys.TASK_STATUSES.TODO.value,
            ...answerPayload(selected, draft),
            optimistic: false,
          })
        )
        if (!ok) {
          setError(true)
          return
        }
        if (hasAnswer) completed.add(selected.st_id)
        else completed.delete(selected.st_id)
      }
      setDrafts((previous) => {
        const next = { ...previous }
        delete next[selected.st_id]
        return next
      })
      showSubtask(nextUnanswered(subtasks, selected.st_id, completed))
    } catch {
      setError(true)
    } finally {
      saveLock.current = false
      setSaving(false)
      setIsSubtaskBusy(false)
    }
  }

  const restoredSelection = context.contentStack.find((frame) => frame.contentType === TaskOverviewContentType.MAIN)
    ?.props?.selectedSubtaskId

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (saveLock.current) setIsSubtaskBusy(false)
    }
    // Busy belongs to this workspace lifetime; late responses cannot unlock its replacement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (review && context.contentStack[context.contentStack.length - 1]?.contentType === TaskOverviewContentType.MAIN)
      review.setSelected(selectedId, false)
  }, [review, selectedId, context.contentStack])

  useEffect(() => {
    if (restoredSelection) select(restoredSelection)
  }, [restoredSelection])

  useEffect(() => {
    if (selectedId && !subtasks.some((subtask) => subtask.st_id === selectedId)) select(null)
  }, [subtasks, selectedId])

  return {
    ...context,
    subtasks,
    allSubtasks,
    selected,
    selectedId,
    draft,
    change,
    choose,
    done,
    isPollSelection,
    retryPoll,
    pollNeedsRetry: isPollSelection && dirty && !busy,
    busy,
    canDone,
    hasUnsavedAnswers,
    error,
    completed,
    remaining,
    resultFor,
  }
}
